"use client"

import Image from "next/image"
import { useState } from "react"
import { HistoryDetailSheet, type HistoryEntry, historyDisplayLabel, userVisibleHistoryEntries } from "./history-detail-sheet.tsx"
import { FormattedPointAmount } from "./point-amount.tsx"
import { PaginationControls } from "./pagination-controls.tsx"
import { RedesignAccountEmpty } from "./redesign-account-empty.tsx"

const historyPageSize = 10
const historyDateFormatter = new Intl.DateTimeFormat("ko-KR", { month: "2-digit", day: "2-digit" })
const historyShortDate = (date: string): string =>
  historyDateFormatter
    .formatToParts(new Date(date))
    .filter(({ type }) => type === "month" || type === "day")
    .map(({ value }) => value)
    .join(".")
const entryKind = (entry: HistoryEntry): string => (entry.label === "포인트 전환" ? "전환" : entry.label === "상품 사용" ? "교환" : entry.label === "잔액 소멸" ? "소멸" : entry.label === "잔액 복원" ? "복원" : "내역")

export const RedesignHistoryList = ({ entries }: { readonly entries: readonly HistoryEntry[] }) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedEntry, setSelectedEntry] = useState<HistoryEntry | null>(null)
  const visibleEntries = userVisibleHistoryEntries(entries)
  const totalPages = Math.max(1, Math.ceil(visibleEntries.length / historyPageSize))
  const activePage = Math.min(currentPage, totalPages)
  const pageEntries = visibleEntries.slice((activePage - 1) * historyPageSize, activePage * historyPageSize)

  if (visibleEntries.length === 0) return <RedesignAccountEmpty kind="history" />

  return (
    <div className="redesign-history-layout" data-history-layout>
      <ol aria-label="시간순 잔액 이력" className="redesign-history-list" data-history-list>
        {pageEntries.map((entry) => (
          <li data-history-row key={entry.id}>
            <button className="redesign-history-row" onClick={() => setSelectedEntry(entry)} type="button">
              <time dateTime={entry.occurredAt}>{historyShortDate(entry.occurredAt)}</time>
              <span className="redesign-history-description">
                <span className="redesign-history-kind" data-kind={entryKind(entry)}>
                  {entryKind(entry)}
                </span>
                <span className="redesign-history-label">{historyDisplayLabel(entry.label, entry.sourceMediaName)}</span>
              </span>
              <strong data-amount-direction={entry.amount.trim().startsWith("-") ? "debit" : entry.amount.trim().startsWith("+") ? "credit" : "neutral"}>
                <FormattedPointAmount value={entry.amount} />
              </strong>
            </button>
          </li>
        ))}
      </ol>
      <PaginationControls currentPage={activePage} label="이용 내역 페이지" onPageChange={setCurrentPage} totalPages={totalPages} />
      <HistoryDetailSheet entry={selectedEntry} onClose={() => setSelectedEntry(null)} />
    </div>
  )
}

export const RedesignBalanceView = ({ details, entries }: { readonly details: readonly { readonly label: string; readonly value: string }[]; readonly entries: readonly HistoryEntry[] }) => {
  const available = details.find(({ label }) => label === "사용 가능")?.value
  const held = details.find(({ label }) => label === "보류")?.value
  const expiry30 = details.find(({ label }) => label === "30일 내 소멸")?.value
  const expiry90 = details.find(({ label }) => label === "90일 내 소멸")?.value

  return (
    <div className="redesign-balance-layout" data-redesign-balance>
      {available === undefined ? null : (
        <section aria-label="잔액 요약" className="redesign-balance-total" data-balance-total>
          <p>사용 가능한 통합 잔액</p>
          <strong>
            <FormattedPointAmount value={available} />
          </strong>
        </section>
      )}
      {expiry30 === undefined && expiry90 === undefined ? null : (
        <section aria-label="소멸 예정 잔액" className="redesign-balance-expiry">
          <details className="redesign-expiry-help">
            <summary>
              <span>소멸 예정</span>
              <span className="redesign-expiry-info-icon">
                <Image alt="소멸 예정 금액 안내" height={15} src="/assets/cashhub/redesign/account-info.svg" width={15} />
              </span>
            </summary>
            <p>각 기간 안에 사용기한이 끝나는 잔액입니다. 90일 내 소멸 금액에는 30일 내 소멸 금액이 포함됩니다.</p>
          </details>
          <div className="redesign-expiry-amounts">
            {expiry30 === undefined ? null : (
              <span>
                <small>D-30</small>
                <strong>
                  <FormattedPointAmount value={expiry30} />
                </strong>
              </span>
            )}
            {expiry90 === undefined ? null : (
              <span>
                <small>D-90</small>
                <strong>
                  <FormattedPointAmount value={expiry90} />
                </strong>
              </span>
            )}
          </div>
        </section>
      )}
      <dl className="redesign-balance-details" data-detail-grid>
        {available === undefined ? null : (
          <div>
            <dt>사용 가능 잔액</dt>
            <dd>
              <FormattedPointAmount value={available} />
            </dd>
          </div>
        )}
        {held === undefined ? null : (
          <div data-balance-supporting>
            <dt>보류 금액</dt>
            <dd>
              <FormattedPointAmount value={held} />
            </dd>
          </div>
        )}
      </dl>
      <div className="redesign-balance-notes">
        <p>· 보류 금액은 처리 결과에 따라 사용 가능 잔액에 반영됩니다.</p>
        <p>· 소멸 금액은 유효기간 만료 시 복구되지 않습니다.</p>
      </div>
      <section aria-label="이용 내역" className="redesign-balance-history">
        <header>
          <h2>이용 내역</h2>
          <p>전환과 사용 내역을 시간순으로 확인하세요.</p>
        </header>
        <RedesignHistoryList entries={entries} />
      </section>
    </div>
  )
}
